import BaseService from "./BaseService";

export type MfaMethod = "authenticatorApp" | "email";

export type MfaStatusResponse = {
    isEnabled: boolean;
    organizationRequiresMfa: boolean;
    requiresMfaSetup: boolean;
    requiresMfaChallenge: boolean;
    canDisable: boolean;
    availableMethods: MfaMethod[];
    enabledMethods: MfaMethod[];
    hasAuthenticatorApp: boolean;
    hasConfirmedEmail: boolean;
    isAuthenticatorAppEnabled: boolean;
    isEmailEnabled: boolean;
    maskedEmail: string;
    recoveryCodesRemaining: number;
};

export type MfaSetupResponse = {
    manualEntryKey: string;
    authenticatorUri: string;
};

export type MfaSetupCompleteResponse = {
    recoveryCodes: string[];
    recoveryCodesRemaining: number;
};

export type MfaEmailSetupResponse = {
    maskedEmail: string;
};

export type MfaChallengeResponse = {
    organizationId: string;
    availableMethods: MfaMethod[];
    isSatisfied: boolean;
};

class MfaService extends BaseService {
    protected urlBase: string = "api/Mfa";

    public async getStatus() {
        return this.get<MfaStatusResponse>(new URLSearchParams(), "Status");
    }

    public async beginManageSetup() {
        return this.post<MfaSetupResponse>(
            null,
            new URLSearchParams(),
            "BeginManageSetup",
        );
    }

    public async completeManageSetup(code: string) {
        return this.post<MfaSetupCompleteResponse>(
            JSON.stringify({ code }),
            new URLSearchParams(),
            "CompleteManageSetup",
        );
    }

    public async beginManageEmailSetup() {
        return this.post<MfaEmailSetupResponse>(
            null,
            new URLSearchParams(),
            "BeginManageEmailSetup",
        );
    }

    public async completeManageEmailSetup(code: string) {
        return this.post<MfaSetupCompleteResponse>(
            JSON.stringify({ code }),
            new URLSearchParams(),
            "CompleteManageEmailSetup",
        );
    }

    public async beginResetAuthenticator(code: string) {
        return this.post<MfaSetupResponse>(
            JSON.stringify({ code }),
            new URLSearchParams(),
            "BeginResetAuthenticator",
        );
    }

    public async completeResetAuthenticator(code: string) {
        return this.post<MfaSetupCompleteResponse>(
            JSON.stringify({ code }),
            new URLSearchParams(),
            "CompleteResetAuthenticator",
        );
    }

    public async beginChallenge() {
        return this.post<MfaChallengeResponse>(
            null,
            new URLSearchParams(),
            "BeginChallenge",
        );
    }

    public async sendEmailCode(organizationId?: string) {
        return this.post<MfaEmailSetupResponse>(
            JSON.stringify({ organizationId }),
            new URLSearchParams(),
            "SendEmailCode",
        );
    }

    public async verify(code: string, organizationId?: string) {
        return this.post<void>(
            JSON.stringify({ code, organizationId }),
            new URLSearchParams(),
            "Verify",
        );
    }

    public async verifyEmailCode(code: string, organizationId?: string) {
        return this.post<void>(
            JSON.stringify({ code, organizationId }),
            new URLSearchParams(),
            "VerifyEmailCode",
        );
    }

    public async regenerateRecoveryCodes() {
        return this.post<MfaSetupCompleteResponse>(
            null,
            new URLSearchParams(),
            "RegenerateRecoveryCodes",
        );
    }

    public async disable() {
        return this.post<MfaStatusResponse>(
            null,
            new URLSearchParams(),
            "Disable",
        );
    }
}

const mfaService = new MfaService();

export default mfaService;
